/* eslint-disable @next/next/no-img-element */
import React, { FunctionComponent, ReactNode, useContext, useState } from 'react'
import classNames from 'classnames'
import styles from './AuthMenu.module.css'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import { GLOBAL_CLASSES } from '@/config/html-attributes.config'
import { Button } from '@/app/_components/Button'
import { Portal } from '@/app/_components/Portal/Portal'
import useIsHydrated from '@/hooks/useIsHydrated'
import TXT from '@/messages'

/**
 * Drops the profile image once it fails to load, so the menu falls back to the "Account" label.
 * Tracks the failed URL rather than a flag, so a different user's image still gets a chance to load.
 */
const useProfileImage = (image?: Nullable<string>): {
  profileImage?: Nullable<string>
  handleImageError: () => void
} => {
  const [failedImage, setFailedImage] = useState<Nullable<string>>()

  return {
    profileImage: image !== failedImage ? image : null,
    handleImageError: () => setFailedImage(image),
  }
}

const AuthMenu: FunctionComponent = (): ReactNode => {
  const {
    user,
    loading,
    setIsLogoutModalOpen,
    handleLogin,
  } = useContext(LoginContext)
  const isHydrated = useIsHydrated()
  const [unloading, setUnloading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const handleLogout = (): void => {
    setUnloading(true)
    setDropdownOpen(false)
    setIsLogoutModalOpen(true)
  }

  const profile = user?.profile
  const { profileImage, handleImageError } = useProfileImage(profile?.image)
  const visibility = loading && !user ? 'hidden' : 'visible'

  if (!isHydrated) return null

  return (
    <Portal selector={`#${PORTAL_SELECTORS.authMenu}`}>
      <div
        className={styles.Wrapper}
        data-testid="auth-menu-wrapper"
        style={{ visibility }}
      >
        <div className={styles.AuthButtonContainer}>
          {profile
            ? (
              <button
                type="button"
                data-testid="auth-menu-show-dropdown"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={classNames(
                  styles.AuthButton,
                  styles.LoggedIn,
                  GLOBAL_CLASSES.NavLink,
                  { [styles.HasImage]: !!(profileImage) },
                )}
                aria-label={TXT.eventsA11y.loggedInUserMenu}
              >
                {profileImage
                  ? (
                    <div
                      className={classNames(
                        styles.UserImage,
                      )}
                      data-testid="auth-menu-user-image"
                    >
                      <img
                        src={profileImage}
                        alt={TXT.eventsA11y.userProfile}
                        // Google's avatar host intermittently rejects hotlinked requests that send a referrer
                        referrerPolicy="no-referrer"
                        onError={handleImageError}
                      />
                    </div>
                  )
                  : (
                    <>{TXT.siteChrome.account}</>
                  )}
              </button>
            )
            : (
              <button
                type="button"
                data-testid="auth-menu-login"
                onClick={handleLogin}
                className={classNames(
                  styles.AuthButton,
                  GLOBAL_CLASSES.NavLink,
                  'login',
                )}
                aria-label={TXT.siteChrome.signIn}
              >
                {TXT.siteChrome.signIn}
              </button>
            )}
        </div>
        {profile && (
          <div
            data-testid="auth-menu-dropdown"
            className={classNames(
              styles.Dropdown,
              GLOBAL_CLASSES.Reset,
              {
                [styles.Visible]: dropdownOpen,
                'is-visible': dropdownOpen, // for testing
              },
            )}
          >
            <div className={styles.UserContainer}>
              <>
                <div
                  className={styles.UserName}
                  data-testid="auth-menu-username"
                >
                  {profile.firstName}
                  {' '}
                  {profile.lastName}
                </div>
                {profile?.email && (
                  <div
                    className={styles.UserEmail}
                    data-testid="auth-menu-email"
                  >
                    {profile?.email}
                  </div>
                )}
              </>
            </div>
            <Button
              className={classNames(styles.Logout)}
              onClick={handleLogout}
              aria-disabled={unloading}
              uppercase
              size="lg"
              weight="bold"
              testId="auth-menu-logout"
            >
              {TXT.customerInfo.logout}
            </Button>
          </div>
        )}
      </div>
      <div
        className={classNames(styles.Overlay, { [styles.Visible]: dropdownOpen })}
        data-testid="auth-menu-overlay"
        onClick={() => {
          setDropdownOpen(false)
        }}
      />
    </Portal>
  )
}

export default AuthMenu
